import React from 'react';
import {
  Alert,
  Button,
  Col,
  Container,
  Row,
} from 'react-bootstrap';
import {OSM, Vector as VectorSource} from 'ol/source';
import {Tile as TileLayer, Vector as VectorLayer} from 'ol/layer';
import OlMap from 'ol/Map';
import View from 'ol/View';
import Draw from 'ol/interaction/Draw';
import {ScaleLine, defaults as defaultControls} from 'ol/control';
import {Point, Polygon} from 'ol/geom';
import Modify from 'ol/interaction/Modify';
import Snap from 'ol/interaction/Snap';
import Overlay from 'ol/Overlay';
import Feature from 'ol/Feature';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import Geocoder from 'ol-geocoder';
import Card from '../../components/Card';
import {
  pointStyle,
  polygonStyle,
  StyledContainer,
  Toolbar,
  vectorLayerStyle,
} from './styles';
import AreaSelectedModal from './AreaSelectedModal';
import {approveSelectedArea, setCoords, setIsTouched} from '../../ducks/area';
import ExampleModal from './ExampleModal';



class SelectArea extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      pointCount: 0,
      exampleModalShow: false,
      modalShow: false,
      featureIsDrawn: false,
      isDrawing: false,
      isAdjusting: false,
      firstPointFeature: false,
      // featureIsClosable
    };

    this.mapRef = React.createRef();

    this.source = new VectorSource();
    this.vector = new VectorLayer({
      source: this.source,
      style: vectorLayerStyle,
    });

    const raster = new TileLayer({
      source: new OSM(),
    });

    const scaleControl = new ScaleLine({
      units: 'metric',
      steps: 4,
      text: true,
      minWidth: 140,
    });

    this.olmap = new OlMap({
      controls: defaultControls().extend([scaleControl]),
      layers: [raster, this.vector],
      target: null,
      view: new View({
        center: [579974.54, 6840911.04],
        zoom: 9,
      }),
    });

    this.geocoder = new Geocoder('nominatim', {
      provider: 'osm',
      placeholder: 'Zoek naar locatie',
      preventDefault: true,
      limit: 5,
      keepOpen: true,
    });

    this.olmap.addControl(this.geocoder);

    this.draw = new Draw({
      source: this.source,
      type: 'Polygon',
      style: polygonStyle,
    });

    this.modify = new Modify({source: this.source});
    this.olmap.addInteraction(this.modify);

    const snap = new Snap({source: this.source});
    this.olmap.addInteraction(snap);

    this.helpTooltip = null;
    this.helpTooltipElement = null;
  }


  componentDidMount() {
    const {
      coords,
    } = this.props;

    this.olmap.setTarget(this.mapRef.current);

    if (coords) {
      const feature = new Feature({
        geometry: new Polygon([coords]),
      });

      this.source.addFeature(feature);

      this.setState({
        featureIsDrawn: true,
        isDrawing: false,
        isAdjusting: false,
      });
    } else {
      // If a feature is already present we don't allow the user to draw another one
      this.olmap.addInteraction(this.draw);
    }

    // Point
    this.pointDraw = new Draw({
      source: this.source,
      type: 'Point',
    });

    this.olmap.addInteraction(this.pointDraw);
    this.source.on('addfeature', this.handleAddPoint);

    this.createHelpTooltip();
    this.geocoder.on('addresschosen', this.handleAdresssChosen);
    this.olmap.on('pointermove', this.pointerMoveHandler);
    this.draw.on('drawstart', this.drawStartHandler);
    this.modify.on('modifyend', this.modifyEndHandler);
    // Initiate this after possible adding a polygon so that the modal doesn't pop up
    this.vector.getSource().on('addfeature', this.handlePolygonAdded);
  }

  componentWillUnmount() {
    this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);

    // draw
    this.olmap.removeInteraction(this.draw);
    this.olmap.removeInteraction(this.pointDraw);
    this.source.un('addfeature', this.handleAddPoint);

    this.geocoder.un('addresschosen', this.handleAdresssChosen);
    this.olmap.un('pointermove', this.pointerMoveHandler);
    this.draw.un('drawstart', this.drawStartHandler);
    this.modify.un('modifyend', this.modifyEndHandler);
    this.vector.getSource().un('addfeature', this.handlePolygonAdded);
  }

  handleAddPoint = (event) => {
    const {pointCount, firstPointFeature} = this.state;

    this.setState({pointCount: pointCount + 1});

    if (pointCount === 0) {
      this.setState({firstPointFeature: event.feature});
    }

    if (pointCount === 2 && firstPointFeature) {
      const firstPointGeometry = firstPointFeature.getGeometry();
      if (firstPointGeometry instanceof Point) {
        firstPointFeature.setStyle(pointStyle);
      }
    }
  };

  handleAdresssChosen = (evt) => {
    const coord = evt.coordinate;

    this.olmap.getView().setCenter(coord);
    this.olmap.getView().setZoom(13);
  };

  handlePolygonAdded = () => {
    const {firstPointFeature} = this.state;

    const polygonFeature = this.source.getFeatures().find(feature => {
      return feature.getGeometry().getType() === 'Polygon'
    })

    if (!polygonFeature) return

    const {
      setCoords,
    } = this.props;

    this.olmap.removeInteraction(this.draw);
    const drawnCoords = polygonFeature.getGeometry()
      .getCoordinates()[0];

    setCoords(drawnCoords);

    firstPointFeature.setStyle(null);

    this.setState({
      modalShow: true,
      featureIsDrawn: true,
      isDrawing: false,
      firstPointFeature: null,
      pointCount: 0,
    });
  };

  modifyEndHandler = () => {
    const {
      setCoords,
    } = this.props;

    const polygonFeature = this.source.getFeatures().find(feature => {
      return feature.getGeometry().getType() === 'Polygon'
    })

    if (!polygonFeature) return

    const drawnCoords = polygonFeature.getGeometry()
      .getCoordinates()[0];

    setCoords(drawnCoords);
  }

  createHelpTooltip = () => {
    if (this.helpTooltipElement) {
      this.helpTooltipElement.parentNode.removeChild(this.helpTooltipElement);
    }
    this.helpTooltipElement = document.createElement('div');
    this.helpTooltipElement.className = 'ol-tooltip hidden';
    this.helpTooltip = new Overlay({
      element: this.helpTooltipElement,
      offset: [15, 0],
      positioning: 'center-left',
    });
    this.olmap.addOverlay(this.helpTooltip);
  };

  pointerMoveHandler = (evt) => {
    const {
      featureIsDrawn,
      isDrawing,
      isAdjusting,
    } = this.state;

    if (evt.dragging) {
      return;
    }

    let helpMsg = null;
    if (!isDrawing && !featureIsDrawn) {
      helpMsg = 'Klik om een gebied te selecteren';
    } else if (isAdjusting) {
      helpMsg = 'Sleep de rand van de selectie om aan te passen';
    }

    this.helpTooltipElement.innerHTML = helpMsg;
    this.helpTooltip.setPosition(evt.coordinate);

    this.helpTooltipElement.classList.remove('hidden');
  };

  clearDrawing = () => {
    const {
      setCoords,
    } = this.props;

    setCoords(null);

    // This clears the existing drawings
    this.source.clear();

    // This gets rid of half-way drawn features which aren't registered
    // on the source yet

    this.olmap.removeInteraction(this.draw);
    this.olmap.addInteraction(this.draw);

    this.setState({
      featureIsDrawn: false,
      isAdjusting: false,
    });
  };

  drawStartHandler = () => {
    this.setState({
      isDrawing: true,
    });
  };

  approveArea = async () => {
    const {
      approveSelectedArea,
      coords,
    } = this.props;

    this.setState({
      modalShow: false,
    });

    approveSelectedArea(coords);
  };

  handleExampleModalHide = () => {
    const {
      setIsTouched,
    } = this.props;

    this.setState({
      exampleModalShow: false,
    });
    setIsTouched(true);
  }

  showExampleModal = () => {
    this.setState({
      exampleModalShow: true,
    });
  }

  render() {
    const {
      error,
      coords,
      isTouched,
    } = this.props;

    const {
      isDrawing,
      modalShow,
      featureIsDrawn,
      isAdjusting,
      exampleModalShow,
    } = this.state;

    return (
      <StyledContainer
        ref={this.mapRef}
        fluid
      >
        <Toolbar show>
          <Container>
            <Row>
              <Col>
                <Card>
                  {
                    error && (<Alert variant="danger">{error}</Alert>)
                  }
                  <div className="d-flex flex-column flex-md-row justify-content-between ">
                    <div>
                      {
                        (coords || (isDrawing || isAdjusting))
                        && (
                          <Button
                            onClick={this.clearDrawing}
                            variant="outline-primary"
                            className="mb-2 mb-md-0 mr-2"
                          >
                            Opnieuw beginnen
                          </Button>
                        )
                      }

                      <Button
                        onClick={this.showExampleModal}
                        variant="outline-secondary"
                        className="mb-2 mb-md-0"
                      >
                        Bekijk instructies
                      </Button>
                    </div>

                    {featureIsDrawn && (
                      <Button
                        disabled={!featureIsDrawn}
                        onClick={this.approveArea}
                        variant="primary"
                      >
                        Gebied selecteren
                      </Button>
                    )}
                  </div>
                </Card>
              </Col>
            </Row>
          </Container>
        </Toolbar>
        <ExampleModal
          open={(exampleModalShow || !(isTouched || error || coords))}
          handleHide={this.handleExampleModalHide}
        />

        <AreaSelectedModal
          coords={coords}
          show={!!(modalShow && coords)}
          handleClick={this.approveArea}
          onHide={() => this.setState({modalShow: false, isAdjusting: true})}
        />
      </StyledContainer>
    );
  }
}

const mapStateToProps = (state) => ({
  error: state.area.error,
  isTouched: state.area.isTouched,
  coords: state.area.coords,
});

const mapDispatchToProps = (dispatch) => ({
  approveSelectedArea: (coords) => dispatch(approveSelectedArea(coords)),
  setCoords: (coords) => dispatch(setCoords(coords)),
  setIsTouched: (value) => dispatch(setIsTouched(value)),
});

SelectArea.propTypes = {
  isTouched: PropTypes.bool,
  error: PropTypes.string,
  coords: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.number,
    ),
  ),
  approveSelectedArea: PropTypes.func.isRequired,
  setCoords: PropTypes.func.isRequired,
};

SelectArea.defaultProps = {
  isTouched: false,
  error: null,
  coords: null,
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectArea);
